import React, { useState } from 'react';
import {
  useAuth,
  generateCleanupReport,
  cleanupAllOrphanedData,
  findOrphanedTrips
} from '@trip-planner-monorepo/shared';

/**
 * Admin Cleanup Component
 *
 * Provides UI for running orphaned data cleanup utilities.
 * This is a utility component for admin/development use.
 *
 * Usage: Navigate to /admin-cleanup in your app
 */
const AdminCleanup = () => {
  const { currentUser } = useAuth();
  const [report, setReport] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateReport = async () => {
    if (!currentUser) {
      setError('Please log in first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('[AdminCleanup] Generating cleanup report...');
      const reportText = await generateCleanupReport(currentUser.uid);
      setReport(reportText);
      console.log('[AdminCleanup] Report generated successfully');
    } catch (err) {
      console.error('[AdminCleanup] Error generating report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = async () => {
    if (!currentUser) {
      setError('Please log in first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('[AdminCleanup] Running dry run...');
      const result = await cleanupAllOrphanedData(currentUser.uid, true);
      setDryRunResult(result);
      console.log('[AdminCleanup] Dry run completed:', result);
    } catch (err) {
      console.error('[AdminCleanup] Error during dry run:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActualCleanup = async () => {
    if (!currentUser) {
      setError('Please log in first');
      return;
    }

    const confirmed = window.confirm(
      'WARNING: This will permanently delete orphaned data.\n\n' +
      'Have you reviewed the dry run results?\n\n' +
      'Click OK to proceed with actual cleanup.'
    );

    if (!confirmed) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[AdminCleanup] Running actual cleanup...');
      const result = await cleanupAllOrphanedData(currentUser.uid, false);
      setCleanupResult(result);
      console.log('[AdminCleanup] Cleanup completed:', result);
    } catch (err) {
      console.error('[AdminCleanup] Error during cleanup:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFindOrphaned = async () => {
    if (!currentUser) {
      setError('Please log in first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('[AdminCleanup] Finding orphaned trips...');
      const orphaned = await findOrphanedTrips(currentUser.uid);
      console.log('[AdminCleanup] Orphaned trips found:', orphaned);
      alert(`Found ${orphaned.length} orphaned trip(s). Check console for details.`);
    } catch (err) {
      console.error('[AdminCleanup] Error finding orphaned trips:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div style={{ padding: '20px' }}>
        <h1>Admin Cleanup Utility</h1>
        <p>Please log in to use cleanup utilities.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Admin Cleanup Utility</h1>
      <p>User: {currentUser.email}</p>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '4px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <h2>Step 1: Generate Cleanup Report</h2>
        <p>See what orphaned data exists (safe, read-only operation)</p>
        <button
          onClick={handleGenerateReport}
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            marginRight: '10px'
          }}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>

        <button
          onClick={handleFindOrphaned}
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: '#9C27B0',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Find Orphaned Trips
        </button>

        {report && (
          <pre style={{
            marginTop: '10px',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            fontSize: '14px'
          }}>
            {report}
          </pre>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2>Step 2: Dry Run (Preview)</h2>
        <p>Preview what would be deleted (safe, no actual deletion)</p>
        <button
          onClick={handleDryRun}
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          {loading ? 'Running...' : 'Run Dry Run'}
        </button>

        {dryRunResult && (
          <div style={{
            marginTop: '10px',
            padding: '15px',
            backgroundColor: '#fff3e0',
            borderRadius: '4px'
          }}>
            <h3>Dry Run Results</h3>
            <pre style={{ fontSize: '14px', overflow: 'auto' }}>
              {JSON.stringify(dryRunResult, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2>Step 3: Actual Cleanup</h2>
        <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
          ⚠️ WARNING: This will permanently delete data!
        </p>
        <button
          onClick={handleActualCleanup}
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          {loading ? 'Cleaning...' : '⚠️ Execute Cleanup'}
        </button>

        {cleanupResult && (
          <div style={{
            marginTop: '10px',
            padding: '15px',
            backgroundColor: '#e8f5e9',
            borderRadius: '4px'
          }}>
            <h3>Cleanup Results</h3>
            <pre style={{ fontSize: '14px', overflow: 'auto' }}>
              {JSON.stringify(cleanupResult, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div style={{
        marginTop: '40px',
        padding: '15px',
        backgroundColor: '#e3f2fd',
        borderRadius: '4px'
      }}>
        <h3>Usage Notes</h3>
        <ul>
          <li>Always run "Generate Report" first to see what data exists</li>
          <li>Run "Dry Run" to preview what would be deleted</li>
          <li>Review dry run results carefully before executing cleanup</li>
          <li>Actual cleanup is permanent and cannot be undone</li>
          <li>Check browser console for detailed logs</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminCleanup;
